import React, { useEffect, useState, useMemo } from "react";
import { Container, Row, Col, Card, CardBody, Label, Input, Button, FormGroup, Modal, ModalHeader, ModalBody, Alert } from "reactstrap";
import Select from "react-select";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getBusinesses } from "../../components/Api/GmsBusiness";
import { getBranches } from "../../components/Api/GmsBranch";
import { getSeparationReasons, saveSeparation, calculateGratuity } from "../../components/Api/GmsEmployeeSeparation";
import { searchEmployees, getEmployeesWithDetails } from "../../components/Api/GmsEmployee";
import { getSalaryAdvanceTransactions } from "../../components/Api/GmsSalaryAdvanceTransaction";
import Swal from "sweetalert2";
import { Search, User, Briefcase, MapPin, Building2, Calendar, FileText, DollarSign, AlertCircle, Info, ShieldCheck } from "lucide-react";
import API_URL from "../../config";
import { hasPagePermissionWithAdmin, isUserAdmin } from "../../components/Permission/PermissionService";

const getUserSession = () => {
    const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const normalizeId = (value) => {
        if (!value || value === 'null') return null;
        return parseInt(value, 10) || null;
    };
    return {
        userId: authUser.userId || user.user_Id || 1,
        businessId: normalizeId(authUser.companyId || user.user_Company_Id),
        branchId: normalizeId(authUser.branchId || user.user_Branch_Id),
    };
};

const EmployeeSeparationPage = () => {
    const session = useMemo(() => getUserSession(), []);
    const permissions = JSON.parse(localStorage.getItem('permissions') || '[]');
    const hasPermission = hasPagePermissionWithAdmin(permissions, '/GmsEmployeeSeparation');
    const isAdmin = isUserAdmin();

    const [loading, setLoading] = useState(false);
    const [businesses, setBusinesses] = useState([]);
    const [branches, setBranches] = useState([]);
    const [reasons, setReasons] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [employeeSearchOptions, setEmployeeSearchOptions] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState(null);
    
    const [advanceBalance, setAdvanceBalance] = useState(0);
    const [gratuityData, setGratuityData] = useState(null);
    
    const [filter, setFilter] = useState({
        businessId: session.businessId,
        branchId: session.branchId
    });

    const [formData, setFormData] = useState({
        separationTypeID: null,
        comment: "",
        separationDate: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        if (!hasPermission) {
            window.location.href = "/Dashboard";
            return;
        }

        const fetchData = async () => {
            try {
                const [bizData, branchData, reasonsData] = await Promise.all([
                    getBusinesses({ business_Active: true }),
                    getBranches({ branch_Active: true }),
                    getSeparationReasons()
                ]);
                setBusinesses(bizData || []);
                setBranches(branchData || []);
                setReasons(reasonsData || []);
            } catch (error) {
                console.error("Error fetching initial data", error);
            }
        };
        fetchData();
    }, [hasPermission]);

    const filteredBranches = useMemo(() => {
        if (!filter.businessId) return [];
        return branches.filter(b => b.branch_Buisness_Id === filter.businessId);
    }, [branches, filter.businessId]);

    const handleEmployeeSearch = (inputValue) => {
        if (inputValue.length < 3) {
            setEmployeeSearchOptions([]);
            return;
        }

        if (searchTimeout) clearTimeout(searchTimeout);

        const timeout = setTimeout(async () => {
            setSearchLoading(true);
            try {
                const response = await searchEmployees(inputValue, 1, 20, filter.businessId, filter.branchId);
                const formattedOptions = response.items
                    .filter(emp => emp.onWork)
                    .map(emp => ({
                        value: String(emp.employee_Id),
                        label: String(`${emp.employee_NameEn} (${emp.emp_No || emp.employee_Code || ''})`),
                        employee: emp
                    }));
                setEmployeeSearchOptions(formattedOptions);
            } catch (error) {
                console.error("Search error", error);
            } finally {
                setSearchLoading(false);
            }
        }, 500); // 500ms debounce
        setSearchTimeout(timeout);
    };

    const handleEmployeeSelect = async (opt) => {
        if (!opt) {
            setSelectedEmployee(null);
            return;
        }
        
        setLoading(true);
        try {
            // Fetch enriched employee details
            const response = await getEmployeesWithDetails({ employee_Id: opt.value });
            let enrichedEmployee = null;
            if (response && response.length > 0) {
                enrichedEmployee = response[0];
            } else {
                enrichedEmployee = opt.employee;
            }

            // Fetch Salary Advance Balance
            try {
                const transactions = await getSalaryAdvanceTransactions({ 
                    employee_Id: opt.value,
                    business_Id: filter.businessId 
                });
                
                let balance = 0;
                if (Array.isArray(transactions)) {
                    transactions.forEach(trans => {
                        if (trans.transactionType === 'SalaryAdvances') {
                            balance += trans.issued_Amount || 0;
                        } else if (trans.transactionType === 'PayrollDetails') {
                            balance -= trans.recovered_Amount || 0;
                        }
                    });
                }
                setAdvanceBalance(balance);
            } catch (err) {
                console.error("Error fetching advance balance", err);
                setAdvanceBalance(0);
            }

            // Fetch Gratuity Calculation
            try {
                const grat = await calculateGratuity(opt.value);
                setGratuityData(grat);
            } catch (err) {
                console.error("Error calculating gratuity", err);
                setGratuityData(null);
            }

            setSelectedEmployee(enrichedEmployee);
        } catch (error) {
            console.error("Error fetching full details", error);
            setSelectedEmployee(opt.employee);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        if (!selectedEmployee) {
            Swal.fire("Error", "Please select an employee", "error");
            return;
        }
        if (!formData.separationTypeID) {
            Swal.fire("Error", "Please select a separation reason", "error");
            return;
        }
        if (!formData.separationDate) {
            Swal.fire("Error", "Please select a separation date", "error");
            return;
        }

        setLoading(true);
        try {
            const payload = {
                Business_Id: filter.businessId,
                Branch_Id: filter.branchId,
                EmployeeID: selectedEmployee.employee_Id,
                SeparationTypeID: formData.separationTypeID,
                Comment: formData.comment,
                SeparationDate: formData.separationDate,
                Gratuity_Amt: gratuityData?.GratuityAmount || 0
            };

            const result = await saveSeparation(payload, "SAVE");
            if (result.id) {
                Swal.fire("Success", result.msg, "success");
                // Reset form
                setSelectedEmployee(null);
                setFormData({
                    separationTypeID: null,
                    comment: "",
                    separationDate: new Date().toISOString().split('T')[0]
                });
            }
        } catch (error) {
            Swal.fire("Error", error.msg || "Failed to save", "error");
        } finally {
            setLoading(false);
        }
    };

    const EmployeeSummary = ({ employee }) => {
        if (!employee) return null;

        const formatDate = (dateString) => {
            if (!dateString) return "N/A";
            const date = new Date(dateString);
            const d = String(date.getDate()).padStart(2, '0');
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const y = date.getFullYear();
            return `${d}/${m}/${y}`;
        };

        const DetailItem = ({ icon: Icon, label, value, color }) => (
            <div className="mb-3">
                <label className="text-muted small mb-0 d-block">
                    {Icon && <Icon size={12} className="me-1" />} {label}
                </label>
                <div className={color ? `text-${color} fw-bold` : "fw-bold"} style={{ fontSize: '13px' }}>
                    {value || "N/A"}
                </div>
            </div>
        );

        return (
            <Card className="mt-3 border shadow-sm h-100">
                <CardBody>
                    <div className="d-flex align-items-start mb-4">
                        <div className="avatar-xl me-4 flex-shrink-0">
                            {employee.employee_ImagePath ? (
                                <img src={`${API_URL}${employee.employee_ImagePath}`} alt="" className="img-thumbnail rounded-circle border-primary shadow-sm" style={{ width: '100px', height: '100px', objectFit: 'cover' }} />
                            ) : (
                                <div className="avatar-title rounded-circle bg-primary-subtle text-primary border border-primary-subtle shadow-sm" style={{ width: '100px', height: '100px' }}>
                                    <User size={50} />
                                </div>
                            )}
                        </div>
                        <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                    <h4 className="mb-1 text-primary fw-bold text-uppercase">{employee.employee_NameEn}</h4>
                                    <div className="text-muted h6 mb-0">
                                        <span className="me-2">{employee.emp_No || employee.employee_Code}</span>
                                        <span className={`badge ${employee.onWork ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'} font-size-11`}>
                                            {employee.onWork ? 'On Work' : 'Off Work'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <Row className="mt-3 g-2">
                                <Col md={3} sm={6}>
                                    <DetailItem icon={Briefcase} label="Designation" value={employee.designation_name || employee.designation} />
                                </Col>
                                <Col md={3} sm={6}>
                                    <DetailItem icon={Building2} label="Department" value={employee.department_name || employee.department} />
                                </Col>
                                <Col md={3} sm={6}>
                                    <DetailItem icon={MapPin} label="Branch" value={employee.branch_name || employee.branch} />
                                </Col>
                                <Col md={3} sm={6}>
                                    <DetailItem icon={Calendar} label="Joining Date" value={formatDate(employee.employee_JoinDate)} />
                                </Col>
                            </Row>
                        </div>
                    </div>

                    <Row>
                        <Col md={12}>
                            <hr className="my-2" />
                            <h6 className="text-primary mb-3 font-size-13"><DollarSign size={14} className="me-1" /> Salary Summary</h6>
                        </Col>
                        {advanceBalance > 0 && (
                            <Col md={12}>
                                <Alert color="danger" className="py-2 px-3 mb-3 border-danger d-flex align-items-center">
                                    <AlertCircle size={18} className="me-2" />
                                    <div>
                                        <strong>Salary Advance Dues: </strong> 
                                        <span className="fs-5 ms-1">{advanceBalance.toLocaleString()}</span>
                                        <div className="small opacity-75 text-dark">This amount must be settled before final separation.</div>
                                    </div>
                                </Alert>
                            </Col>
                        )}
                        <Col md={4}><DetailItem label="Basic Salary" value={employee.employee_BS?.toLocaleString()} /></Col>
                        <Col md={4}><DetailItem label="HRA" value={employee.employee_HRA?.toLocaleString()} /></Col>
                        <Col md={4}><DetailItem label="Transport" value={employee.employee_TA?.toLocaleString()} /></Col>
                        <Col md={4}><DetailItem label="Food" value={employee.employee_FoodAllowance?.toLocaleString()} /></Col>
                        <Col md={4}><DetailItem label="Communication" value={employee.employee_CommunicationAllowance?.toLocaleString()} /></Col>
                        <Col md={4}><DetailItem label="Utility" value={employee.employee_UtilityAllowance?.toLocaleString()} /></Col>
                        <Col md={4}><DetailItem label="Other" value={employee.employee_OA?.toLocaleString()} /></Col>
                        <Col md={4}><DetailItem label="WPS Salary" value={employee.wpsSalary?.toLocaleString()} /></Col>
                        <Col md={4}><DetailItem label="Gross Salary" value={employee.employee_GS?.toLocaleString()} color="success" /></Col>

                        <Col md={12}>
                            <hr className="my-2" />
                            <h6 className="text-primary mb-3 font-size-13">Contact & Identification</h6>
                        </Col>
                        <Col md={6}>
                            <DetailItem icon={MapPin} label="Country" value={employee.country_name} />
                        </Col>
                        <Col md={6}>
                            <DetailItem icon={FileText} label="Phone Number" value={employee.employee_ContactNumber1} />
                        </Col>
                        <Col md={12}>
                            <DetailItem icon={MapPin} label="Address" value={`${employee.employee_Address1 || ''} ${employee.employee_Address2 || ''}`} />
                        </Col>
                        
                        <Col md={12}>
                            <hr className="my-2" />
                            <h6 className="text-primary mb-3 font-size-13">Document Details</h6>
                        </Col>
                        <Col md={6}>
                            <DetailItem label="Passport" value={`${employee.employee_PassportNumber || ''} (Exp: ${formatDate(employee.employee_Passport_ExpDate)})`} />
                        </Col>
                        <Col md={6}>
                            <DetailItem label="National ID" value={`${employee.employee_NatID || ''} (Exp: ${formatDate(employee.employee_NatID_ExpDate)})`} />
                        </Col>
                        <Col md={6}>
                            <DetailItem label="Visa" value={`${employee.employee_VisaNumber || ''} (Exp: ${formatDate(employee.employee_Visa_ExpDate)})`} />
                        </Col>
                        <Col md={6}>
                            <DetailItem label="Labour Card" value={`${employee.employee_LabourCardNumber || ''} (Exp: ${formatDate(employee.employee_LabourCardExpiry)})`} />
                        </Col>
                    </Row>
                </CardBody>
            </Card>
        );
    };

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Breadcrumbs title="Human Resources" breadcrumbItem="Employee Separation" />
                    
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody>
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Company</Label>
                                                <Select
                                                    options={businesses.map(b => ({ value: String(b.business_Id), label: String(b.business_NameEn) }))}
                                                    value={businesses.filter(b => b.business_Id === filter.businessId).map(b => ({ value: String(b.business_Id), label: String(b.business_NameEn) }))}
                                                    onChange={(opt) => setFilter({ ...filter, businessId: opt ? parseInt(opt.value) : null, branchId: null })}
                                                    isDisabled={!isAdmin && !!session.businessId}
                                                    placeholder="Select Company"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Branch</Label>
                                                <Select
                                                    options={filteredBranches.map(b => ({ value: String(b.branch_Id), label: String(b.branch_NameEn) }))}
                                                    value={filteredBranches.filter(b => b.branch_Id === filter.branchId).map(b => ({ value: String(b.branch_Id), label: String(b.branch_NameEn) }))}
                                                    onChange={(opt) => setFilter({ ...filter, branchId: opt ? parseInt(opt.value) : null })}
                                                    isDisabled={!isAdmin && !!session.branchId}
                                                    placeholder="Select Branch"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md={4}>
                                            <FormGroup>
                                                <Label>Search Employee (Type 3 chars)</Label>
                                                <Select
                                                    onInputChange={(val, { action }) => {
                                                        if (action === "input-change") handleEmployeeSearch(val);
                                                    }}
                                                    options={employeeSearchOptions}
                                                    onChange={(opt) => handleEmployeeSelect(opt)}
                                                    isLoading={searchLoading}
                                                    placeholder="Search by Name or Code"
                                                    isClearable
                                                    filterOption={() => true} // Disable local filtering as we do it server-side
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    {selectedEmployee && (
                                        <Row>
                                            <Col lg={7}>
                                                <EmployeeSummary employee={selectedEmployee} />
                                            </Col>
                                            <Col lg={5}>
                                                <Card className="mt-3 border shadow-sm h-100">
                                                    <CardBody>
                                                        <h6 className="text-primary mb-3"><FileText size={16} className="me-1" /> Separation Process</h6>
                                                        
                                                        <FormGroup className="mb-3">
                                                            <Label>Separation Date</Label>
                                                            <Input 
                                                                type="date" 
                                                                value={formData.separationDate}
                                                                onChange={(e) => setFormData({ ...formData, separationDate: e.target.value })}
                                                            />
                                                        </FormGroup>

                                                        <FormGroup className="mb-3">
                                                            <Label>Reason Type</Label>
                                                            <Select 
                                                                options={reasons.map(r => ({ value: String(r.separationReasonID), label: String(r.reasonName) }))}
                                                                value={reasons.filter(r => r.separationReasonID === formData.separationTypeID).map(r => ({ value: String(r.separationReasonID), label: String(r.reasonName) }))}
                                                                onChange={(opt) => setFormData({ ...formData, separationTypeID: opt ? parseInt(opt.value) : null })}
                                                                placeholder="Select Reason"
                                                            />
                                                        </FormGroup>

                                                        <FormGroup className="mb-4">
                                                            <Label>Comment / Details</Label>
                                                            <Input 
                                                                type="textarea" 
                                                                rows={4} 
                                                                placeholder="Enter detailed reason..."
                                                                value={formData.comment}
                                                                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                                                            />
                                                        </FormGroup>

                                                        {gratuityData && (
                                                            <div className="mt-4 p-3 rounded border bg-light shadow-sm mb-4">
                                                                <h6 className="text-primary mb-3 d-flex align-items-center fw-bold">
                                                                    <ShieldCheck size={18} className="me-2" /> Gratuity Calculation Summary
                                                                </h6>
                                                                
                                                                {!gratuityData.IsEligible ? (
                                                                    <Alert color="warning" className="border-warning d-flex align-items-start mb-0">
                                                                        <Info size={18} className="me-2 mt-1 flex-shrink-0" />
                                                                        <div>
                                                                            <div className="fw-bold">Currently Not Eligible for Gratuity</div>
                                                                            <div className="small">
                                                                                Employee has not completed 1 year of continuous service excluding unpaid leaves.
                                                                                <br />
                                                                                <div className="mt-1">
                                                                                    <strong>Current Tenure:</strong> {gratuityData.Years}y {gratuityData.Months}m {gratuityData.Days}d
                                                                                    <span className="mx-2">|</span>
                                                                                    <strong>Remaining:</strong> <span className="text-danger fw-bold">{gratuityData.DaysToEligibility} days</span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Alert>
                                                                ) : (
                                                                    <Row className="g-3">
                                                                        <Col md={4} xs={6}>
                                                                            <div className="text-muted small">Service Period</div>
                                                                            <div className="fw-bold fs-5 text-nowrap">
                                                                                {gratuityData.Years}y {gratuityData.Months}m {gratuityData.Days}d
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={4} xs={6}>
                                                                            <div className="text-muted small">Unpaid Leaves</div>
                                                                            <div className="fw-bold text-danger fs-5">{gratuityData.UnpaidLeaveDays} <span className="small font-weight-normal">Days</span></div>
                                                                        </Col>
                                                                        <Col md={4} xs={12}>
                                                                            <div className="text-muted small">Rate Rule</div>
                                                                            <div className="fw-bold text-info">
                                                                                {gratuityData.TotalServiceDays >= 1825 ? "30 Days Per Year" : "21 Days Per Year"}
                                                                            </div>
                                                                        </Col>
                                                                        <Col md={12}>
                                                                            <hr className="my-2 border-dashed" />
                                                                            <div className="d-flex justify-content-between align-items-center bg-white p-2 rounded border">
                                                                                <div>
                                                                                    <div className="fw-bold text-uppercase small text-muted">Gratuity Amount (Payable)</div>
                                                                                    <div className="small text-muted">Based on Basic: {gratuityData.BasicSalary?.toLocaleString()}</div>
                                                                                </div>
                                                                                <div className="h3 mb-0 text-success fw-bold">
                                                                                    {gratuityData.GratuityAmount?.toLocaleString(undefined, { minimumFractionDigits: 3 })}
                                                                                </div>
                                                                            </div>
                                                                        </Col>
                                                                    </Row>
                                                                )}
                                                            </div>
                                                        )}

                                                        <Button 
                                                            color="primary" 
                                                            className="w-100" 
                                                            size="lg"
                                                            disabled={loading}
                                                            onClick={handleSave}
                                                        >
                                                            {loading ? "Processing..." : "Submit Separation Request"}
                                                        </Button>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        </Row>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default EmployeeSeparationPage;
